import { Component, Input, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { BehaviorSubject, firstValueFrom, Subject } from "rxjs";
import { InputBase } from "common/form/input/types/input-base";
import { FeedbackService } from "chatbot/chatbot-message-feedback.service";
import { AsyncPipe } from "@angular/common";
import { ButtonComponent } from "common/buttons/button.component";
import { InputComponent } from "common/form/input/input.component";

interface FeedbackState {
	isSubmitting: boolean;
	isCommentVisible: boolean;
	submitted: boolean;
	error: boolean;
}

@Component({
	selector: "stages-chatbot-message-toolbar",
	standalone: true,
	imports: [TranslateModule, ReactiveFormsModule, AsyncPipe, ButtonComponent, InputComponent],
	templateUrl: "./chatbot-message-toolbar.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ["./chatbot-message-toolbar.component.scss"],
})
export class ChatbotMessageToolbarComponent implements OnDestroy {
	@Input() chatGuid!: string;
	@Input() feedbackAllowed: boolean = false;
	@Input() userRequest!: string;
	@Input() chatbotResponse!: string;
	@Input() copyTarget!: HTMLElement;

	// Visual feedback when text was copied
	copiedState = false;

	// "Thanks" popup state (controlled elsewhere in the component)
	showThanks = false;
	showError = false;
	private popupHideTimer?: ReturnType<typeof setTimeout>;

	// Form with an optional comment (shown for negative feedback)
	protected feedbackForm: FormGroup;
	protected commentInput = new InputBase({
		key: "comment",
		type: "textarea",
		translateKey: "chatbot.history.feedback.comment.placeholder",
		hidden: false,
		focus: false,
	});

	// Component state management
	private destroy$ = new Subject<boolean>();
	protected state$ = new BehaviorSubject<FeedbackState>({
		isSubmitting: false,
		isCommentVisible: false,
		submitted: false,
		error: false,
	});

	// null = not selected, true = thumbs up, false = thumbs down
	protected selectedFeedback: boolean | null = null;

	// Local config
	private readonly copyResetDelayMs = 1500;

	constructor(
		private fb: FormBuilder,
		private feedbackService: FeedbackService,
		private translate: TranslateService,
	) {
		this.feedbackForm = this.fb.group({
			[this.commentInput.key]: ["", this.commentInput.validators],
		});
	}

	/**
	 * Copies both plaintext and HTML of the target element to the clipboard (if supported).
	 * Falls back to plaintext copy if rich clipboard is not available.
	 */
	async handleCopy(): Promise<void> {
		try {
			const plainText = this.copyTarget?.textContent ?? "";
			const htmlText = this.copyTarget?.innerHTML ?? "";

			// Try rich clipboard first (HTML + plain)
			if (window.ClipboardItem) {
				const clipboardItem = new ClipboardItem({
					"text/plain": new Blob([plainText], { type: "text/plain" }),
					"text/html": new Blob([htmlText], { type: "text/html" }),
				});
				await navigator.clipboard.write([clipboardItem]);
			} else {
				// Fallback to plain text copy
				await navigator.clipboard.writeText(plainText);
			}

			this.copiedState = true;
		} catch (e) {
			// Keep a concise error for diagnostics; avoid breaking UX
			console.error("Copy failed", e);
		} finally {
			this.scheduleCopiedReset();
		}
	}

	/**
	 * Resets the "copied" visual state after a short delay.
	 */
	private scheduleCopiedReset(): void {
		setTimeout(() => (this.copiedState = false), this.copyResetDelayMs);
	}

	/**
	 * Handles positive feedback (thumbs up).
	 * Immediately submits feedback if not busy or already submitted.
	 */
	rateUp(): void {
		if (this.isBusyOrSubmitted()) return;

		this.selectedFeedback = true;
		this.patchState({ isCommentVisible: false });

		this.submitFeedback(true);
	}

	/**
	 * Handles negative feedback (thumbs down).
	 * Shows comment field before submission.
	 */
	rateDown(): void {
		if (this.isBusyOrSubmitted()) return;

		this.selectedFeedback = false;
		// For negative feedback, show comment field first
		this.patchState({ isCommentVisible: true });
	}

	/**
	 * Submits negative feedback from the comment form.
	 * Sends thumbs-down together with the comment (if any) when the form is valid.
	 */

	protected submitNegativeFeedbackForm(): void {
		if (this.feedbackForm.valid) {
			this.submitFeedback(false, this.feedbackForm.get(this.commentInput.key)?.value);
		}
	}

	/**
	 * Cancels current feedback editing state (clears form and hides comment).
	 */
	protected onCancel(): void {
		this.feedbackForm.reset();
		this.selectedFeedback = null;
		this.patchState({ isCommentVisible: false });
		this.showError = false;
	}

	/**
	 * Returns a localized template for a feedback ticket description.
	 * The template uses %s placeholders for values injected later.
	 */
	async getFeedbackTicketDescriptionTemplate(): Promise<string> {
		const userCommentTitle = await firstValueFrom(
			this.translate.get("chatbot.history.feedback.ticket.description.userComment.title"),
		);
		const chatLogTitle = await firstValueFrom(
			this.translate.get("chatbot.history.feedback.ticket.description.chatLog.title"),
		);
		const chatLogSource = await firstValueFrom(
			this.translate.get("chatbot.history.feedback.ticket.description.chatLog.source"),
		);
		const chatLogQuestionTitle = await firstValueFrom(
			this.translate.get("chatbot.history.feedback.ticket.description.chatLog.question"),
		);
		const chatLogAnswerTitle = await firstValueFrom(
			this.translate.get("chatbot.history.feedback.ticket.description.chatLog.answer"),
		);

		return `
			h3. ${userCommentTitle}
			%s
		
			h3. ${chatLogTitle}
			${chatLogSource} '_%s_'
			${chatLogQuestionTitle}:
			{quote}%s{quote}
			${chatLogAnswerTitle}
			{quote}%s{quote}
		`;
	}

	/**
	 * Submits feedback and updates UI state.
	 */
	private async submitFeedback(positive: boolean, comment?: string): Promise<void> {
		this.patchState({ isSubmitting: true });

		try {
			await this.feedbackService.submitFeedback({
				chatGuid: this.chatGuid ?? "",
				userRequest: this.userRequest,
				chatbotResponse: this.chatbotResponse,
				positive: positive,
				comment: comment ?? null,
				ticketSummary: await firstValueFrom(this.translate.get("chatbot.history.feedback.ticket.summary")), //"chatbot.history.feedback.ticket.summary",
				ticketDescriptionTemplate: await this.getFeedbackTicketDescriptionTemplate(),
			});

			this.patchState({
				isSubmitting: false,
				isCommentVisible: false,
				submitted: true,
			});

			this.showError = false;
			this.showThanks = true;
		} catch (e) {
			this.patchState({ isSubmitting: false, error: true });
			this.showError = true;
		}

		if (this.popupHideTimer) clearTimeout(this.popupHideTimer);
		this.popupHideTimer = setTimeout(() => {
			this.showThanks = false;
		}, 1500);
	}

	/**
	 * Returns true if the component is currently submitting or feedback was already submitted.
	 */
	private isBusyOrSubmitted(): boolean {
		const { isSubmitting, submitted } = this.state$.value;
		return isSubmitting || submitted;
	}

	/**
	 * Applies a partial state patch and emits the new state.
	 */
	private patchState(patch: Partial<FeedbackState>): void {
		this.state$.next({ ...this.state$.value, ...patch });
	}

	ngOnDestroy(): void {
		// Clean up timers and notifiers
		if (this.popupHideTimer) {
			clearTimeout(this.popupHideTimer);
			this.popupHideTimer = undefined;
		}
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}
